import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { Box, Flex } from '../ui/Layout.js'
import { loadVideoLite, formatSeconds, loadVideoMP4, baseUrl } from '../helpers.js'
import { gaEvent, trackEvent } from '../tracking.js'
import { getChevronStyling, getChevronImg, getChevronImgSize } from './chevronStyles.js'
import { getAspectRatio } from './EmbedCarousel.js'
import { pauseOtherInlines } from './InlineSingle.js'
import { ProgressBar } from './ui/ProgressBar.js'
import InlinePlayButton from './InlinePlayButton'

const SlideItem = styled(Box)`
  @keyframes loading-gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: -100% 50%;
    }
    100% {
      background-position: -200% 50%;
    }
  }
  cursor: pointer;
  border-radius: ${(props) => `${props.borderRadius}px`};
  box-sizing: border-box;
`

const ImageIconContainer = styled(Flex)`
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 40px !important;
  height: 40px !important;
  border-radius: 40px !important;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  background-color: rgba(78, 78, 78, 0.4) !important;
`

const VideoImage = styled.img`
  box-sizing: border-box;
  border-radius: ${(props) => `${props.borderRadius}px`};
  object-fit: cover;
  width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  margin-top: 0px !important;
  margin-left: 0px !important;
  margin-right: 0px !important;
  margin-bottom: 0px !important;
  ${(props) =>
    props.widgetThumbnailBorder
      ? `border: ${props.widgetThumbnailBorderSize || 3}px solid ${props.widgetThumbnailBorderColor || 'black'};`
      : ''}
  background: linear-gradient(to right, #e9e9e9, #f9f9f9, #e9e9e9);
  background: -webkit-linear-gradient(to right, #e9e9e9, #f9f9f9, #e9e9e9);
  animation: loading-gradient 4s linear infinite;
  background-size: 200%;
`

const VideoContainer = styled(Flex)`
  transition: opacity 0.5s;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Video = styled.video`
  background: transparent;
  cursor: pointer !important;
  transition: opacity 0.1s ease-in-out;
  -moz-transition: opacity 0.1s ease-in-out;
  -webkit-transition: opacity 0.1s ease-in-out;
  box-sizing: border-box;
  border-radius: ${(props) => `${props.borderRadius}px`};
  width: ${(props) => props.videoWidth}px !important;
  height: ${(props) => props.videoHeight}px !important;
  object-fit: cover;
  z-index: 1;
  min-height: 102%;
  min-width: 102%;
  ${(props) =>
    props.isCircle && props.widgetThumbnailBorder && `border: 2.5px solid white; box-sizing: border-box; border-radius: 100%;`}
  &&::-webkit-media-controls {
    display: none !important;
  }
  /* Could Use thise as well for Individual Controls */
  &&::-webkit-media-controls-play-button {
    display: none !important;
  }
  &&::-webkit-media-controls-volume-slider {
    display: none !important;
  }
  &&::-webkit-media-controls-mute-button {
    display: none !important;
  }
  &&::-webkit-media-controls-timeline {
    display: none !important;
  }
  &&::-webkit-media-controls-current-time-display {
    display: none !important;
  }
  ${(props) =>
    props.widgetThumbnailBorder
      ? `border: ${props.widgetThumbnailBorderSize || 3}px solid ${props.widgetThumbnailBorderColor || 'black'};`
      : ''}
  -webkit-mask-image: -webkit-radial-gradient(white, black);
  will-change: transform;
`

const VideoTitle = styled.p`
  font-family: ${(props) => (props.widgetTitleFontFamily ? props.widgetTitleFontFamily.replace(';') : 'inherit')} !important;
  font-weight: ${(props) => props.widgetTitleFontWeight || '700'} !important;
  font-size: ${(props) => props.widgetDesktopTitleFontSize || '18'}px !important;
  line-height: ${(props) => props.widgetDesktopTitleFontSize || '18'}px !important;
  text-align: ${(props) => (props.widgetTitleAlignment ? props.widgetTitleAlignment.toLowerCase() : 'center')} !important;
  color: ${(props) => (props.isInnerTitles ? 'white' : props.widgetTitleFontColor || 'black')};
  text-decoration: none !important;
  margin-bottom: 8px !important;
  margin-top: 0px !important;
  padding: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  word-break: break-word;
`

const VideoSubtitle = styled.p`
  font-family: ${(props) =>
    props.widgetSubtitleFontFamily ? props.widgetSubtitleFontFamily.replace(';') : 'inherit'} !important;
  font-weight: ${(props) => props.widgetSubtitleFontWeight || '500'} !important;
  font-size: ${(props) => props.widgetDesktopSubtitleFontSize || '18'}px !important;
  line-height: ${(props) => props.widgetDesktopSubtitleFontSize || '18'}px !important;
  text-align: ${(props) => (props.widgetTitleAlignment ? props.widgetTitleAlignment.toLowerCase() : 'center')} !important;
  color: ${(props) => (props.isInnerTitles ? 'white' : props.widgetSubtitleFontColor || 'black')};
  text-decoration: none !important;
  margin-bottom: 16px !important;
  margin-top: 0px !important;
  padding: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  word-break: break-word;
`

const TitleContainer = styled(Flex)`
  flex-direction: column;
`

const MuteContainer = styled(Flex)`
  width: 32px;
  height: 32px;
  position: absolute;
  right: 12px;
  top: 16px;
  z-index: 110001;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  background-color: rgba(78, 78, 78, 0.4) !important;
`

const Volume = styled.img`
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  object-fit: none;
  margin-left: ${(props) => props.ml || 0}px;
`

const Titles = ({
  videoWidth,
  gapWidth,
  titleVideoObject,
  widgetTitleAlignment,
  widgetDesktopTitleFontSize,
  widgetTitleFontColor,
  widgetTitleFontFamily,
  widgetTitleFontWeight,
  widgetSubtitleFontWeight,
  widgetSubtitleFontFamily,
  widgetSubtitleFontColor,
  widgetDesktopSubtitleFontSize,
  hideMarginLeft,
  hideMarginRight,
  isMobile,
  widgetMobileTitleFontSize,
  widgetMobileSubtitleFontSize,
  widgetTitlePosition
}) => {
  const isInnerTitles = widgetTitlePosition === 'INSIDE'
  return (
    <TitleContainer
      maxWidth={videoWidth}
      mr={hideMarginRight ? 0 : `${gapWidth / 2}px`}
      ml={hideMarginLeft ? 0 : `${gapWidth / 2}px`}
      width="auto"
    >
      {titleVideoObject.title && (
        <VideoTitle
          widgetTitleAlignment={widgetTitleAlignment}
          widgetDesktopTitleFontSize={isMobile ? widgetMobileTitleFontSize : widgetDesktopTitleFontSize}
          widgetTitleFontColor={widgetTitleFontColor}
          widgetTitleFontFamily={widgetTitleFontFamily}
          widgetTitleFontWeight={widgetTitleFontWeight}
          isInnerTitles={isInnerTitles}
        >
          {titleVideoObject.title}
        </VideoTitle>
      )}
      {titleVideoObject.subtitle && (
        <VideoSubtitle
          widgetTitleAlignment={widgetTitleAlignment}
          widgetDesktopSubtitleFontSize={isMobile ? widgetMobileSubtitleFontSize : widgetDesktopSubtitleFontSize}
          widgetSubtitleFontColor={widgetSubtitleFontColor}
          widgetSubtitleFontFamily={widgetSubtitleFontFamily}
          widgetSubtitleFontWeight={widgetSubtitleFontWeight}
          isInnerTitles={isInnerTitles}
        >
          {titleVideoObject.subtitle}
        </VideoSubtitle>
      )}
    </TitleContainer>
  )
}

const StyledPicture = styled.picture`
  display: block;
  height: 100% !important;
`

const StyledNextPicture = styled.picture`
  z-index: -1;
  height: 100% !important;
  position: absolute;
  left: 0;
  top: 0;
`

const CursorDiv = styled.div`
  cursor: pointer !important;
`

const InnerTitleContainer = styled(Flex)`
  box-sizing: border-box !important;
  z-index: 1;
  position: absolute;
  bottom: 0px;
  left: 0px;
  padding-left: 16px;
  padding-right: 16px;
  height: 150px;
  flex-direction: column-reverse;
  width: 100%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%);
`

const InlineSlideItem = ({
  campaign,
  videoHeight,
  videoWidth,
  activeVideoIndex,
  videosToShow,
  i,
  video,
  videoObjects,
  borderRadius,
  isStories,
  isCircle,
  isMobile,
  isNavigation,
  noNextImage,
  isMuted,
  setIsMuted,
  isActive,
  isPaused,
  setIsPaused,
  setActive,
  hasStarted,
  setHasStarted
}) => {
  const {
    widgetTitleActive,
    widgetTitlePosition,
    widgetGapWidth,
    widgetThumbnailBorderSize,
    widgetThumbnailBorder,
    widgetThumbnailBorderColor,
    widgetAutoplay,
    widgetShowProgressBar,
    videos
  } = campaign
  const [videoPlaying, setVideoPlaying] = useState(false)
  const isVideo = video.type === 'VIDEO'
  const showNextImage = i === Math.ceil(videosToShow + activeVideoIndex - 1) && !noNextImage
  const imageBoostFactor = 1.5
  const customThumbnailRaw = isMobile && video.customMobileThumbnail ? video.customMobileThumbnail : video.customThumbnail
  const isBunnyCdn = customThumbnailRaw && customThumbnailRaw.includes('clipara.b-cdn.net')
  const customThumbnailUrl =
    video.customThumbnail || video.customMobileThumbnail
      ? isBunnyCdn
        ? customThumbnailRaw
        : customThumbnailRaw.replace('https://ucarecdn.com/', 'https://customthumbnail.getclipara.com/')
      : undefined
  const imageUrl = customThumbnailUrl
    ? isBunnyCdn
      ? `${customThumbnailUrl}?width=${Math.round(videoWidth * imageBoostFactor)}&height=${Math.round(
          videoHeight * imageBoostFactor
        )}`
      : customThumbnailUrl
    : `https://image.mux.com/${video.playbackId}/thumbnail.png?time=${video.thumbnailTime || 0.0}&width=${Math.round(
        videoWidth * imageBoostFactor
      )}&height=${Math.round(videoHeight * imageBoostFactor)}&fit_mode=crop`
  const webpUrl = customThumbnailUrl
    ? isBunnyCdn
      ? imageUrl
      : customThumbnailUrl
    : imageUrl.replace('.png', '.webp')

  // get next image url for lazy load
  let nextImageUrl, nextWebpUrl, nextImageIsVideo, nextImageImageUrl
  if (videos[i + 1]) {
    nextImageIsVideo = videos[i + 1].type === 'VIDEO'
    if (!nextImageIsVideo) {
      nextImageImageUrl = `https://clipara.b-cdn.net/${videos[i + 1].path}?aspect_ratio=${getAspectRatio(
        campaign.widgetThumbnailDimensions
      )}&width=${Math.round(videoWidth * 2)}`
    }
    const nextCustomThumbnailRaw =
      isMobile && videos[i + 1].customMobileThumbnail ? videos[i + 1].customMobileThumbnail : videos[i + 1].customThumbnail
    const nextIsBunnyCdn = nextCustomThumbnailRaw && nextCustomThumbnailRaw.includes('clipara.b-cdn.net')
    const nextCustomThumbnailUrl =
      videos[i + 1].customThumbnail || videos[i + 1].customMobileThumbnail
        ? nextIsBunnyCdn
          ? nextCustomThumbnailRaw
          : nextCustomThumbnailRaw &&
            nextCustomThumbnailRaw.replace('https://ucarecdn.com/', 'https://customthumbnail.getclipara.com/')
        : undefined
    nextImageUrl = nextCustomThumbnailUrl
      ? nextIsBunnyCdn
        ? `${nextCustomThumbnailUrl}?width=${Math.round(videoWidth * imageBoostFactor)}&height=${Math.round(
            videoHeight * imageBoostFactor
          )}`
        : nextCustomThumbnailUrl
      : `https://image.mux.com/${videos[i + 1].playbackId}/thumbnail.png?time=${
          videos[i + 1].thumbnailTime || 0.0
        }&width=${Math.round(videoWidth * imageBoostFactor)}&height=${Math.round(videoHeight * imageBoostFactor)}&fit_mode=crop`
    nextWebpUrl = nextCustomThumbnailUrl
      ? nextIsBunnyCdn
        ? nextImageUrl
        : nextCustomThumbnailUrl
      : nextImageUrl.replace('.png', '.webp')
  }

  const playerId = `__clipara-inline-carousel-${video.id}-${campaign.id}`
  const videoRef = useRef()
  const playPause = () => {
    const element = document.getElementById(playerId)
    if (!element || !isVideo) {
      return
    }
    pauseOtherInlines(playerId)

    if (!element.paused) {
      setIsPaused(true)

      element.pause()
      // element.style.opacity = '0'
      return
    } else if (element.paused) {
      setIsPaused(false)
      if (element.src) {
        setHasStarted(true)
        element.play()
        element.style.opacity = '1'
        return
      } else {
        gaEvent({
          isGaEnabled: campaign?.gaTracking,
          name: 'clipara_video_play',
          data: {
            campaign_name: campaign?.name,
            video_name: video?.name
          }
        })
        trackEvent({
          type: 'PLAY',
          campaignId: campaign.id,
          videoId: video.id,
          playerType: campaign.autoOpen ? 'SHARE' : 'WIDGET',
          widgetId: campaign.widgetId,
          productIds: campaign.videoProducts.filter((p) => p.videoId === video.id).map((p) => p.product.id)
        })
        loadVideoMP4({
          id: playerId,
          video,
          campaign,
          playerName: 'inline-carousel',
          useClip: false,
          videoWidth,
          setHasPlayed: (x) => setHasStarted(x),
          useHigh: campaign.organisationId !== 1038
        })
        element.setAttribute('oncontextmenu', 'return false;')
        element.style.opacity = '1'
        return
      }
    }
  }

  const titleVideoObject = videoObjects.find((vo) => vo.videoId.toString() === video.id.toString() && vo.type === 'TITLE') || {}
  const buttonVideoObject = videoObjects.find((vo) => vo.videoId.toString() === video.id.toString() && vo.type === 'BUTTON') || {}
  return (
    <SlideItem key={video.id} className="keen-slider__slide" borderRadius={borderRadius}>
      <CursorDiv
        href={isNavigation ? buttonVideoObject.buttonLink : undefined}
        onClick={() => {
          setHasStarted(true)
          isMobile && setIsMuted(false)
          if (isActive) {
            playPause()
          } else {
            setActive()
          }
        }}
      >
        <Box position="relative" height={videoHeight} overflow="hidden" borderRadius={borderRadius}>
          {isActive ? (
            <VideoContainer isMobile={isMobile} isStories={isStories} videoPlaying={videoPlaying}>
              <Video
                widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                widgetThumbnailBorder={widgetThumbnailBorder}
                borderRadius={borderRadius}
                videoWidth={videoWidth}
                videoHeight={videoHeight}
                width={videoWidth}
                height={videoHeight}
                minHeight={videoHeight}
                loop
                isCircle={isCircle}
                muted={true}
                id={`__clipara-inline-carousel-clip-${video.id}-${campaign.id}`}
                preload="auto"
                playsInline
                poster={navigator.userAgent.includes('wv') ? imageUrl : undefined}
                webkit-playsInline
                x-webkit-airplay="allow"
              />
            </VideoContainer>
          ) : null}
          <VideoContainer isMobile={isMobile} isStories={isStories} videoPlaying={videoPlaying}>
            <Video
              ref={videoRef}
              widgetThumbnailBorderSize={widgetThumbnailBorderSize}
              widgetThumbnailBorderColor={widgetThumbnailBorderColor}
              widgetThumbnailBorder={widgetThumbnailBorder}
              borderRadius={borderRadius}
              videoWidth={videoWidth}
              videoHeight={videoHeight}
              width={videoWidth}
              height={videoHeight}
              minHeight={videoHeight}
              muted={isMuted}
              loop
              isCircle={isCircle}
              id={`__clipara-inline-carousel-${video.id}-${campaign.id}`}
              preload="auto"
              playsInline
              poster={navigator.userAgent.includes('wv') ? imageUrl : undefined}
              webkit-playsInline
              x-webkit-airplay="allow"
            />
          </VideoContainer>
          {isVideo ? (
            <StyledPicture>
              <source srcset={webpUrl} type="image/webp" />
              <source srcset={imageUrl} type="image/png" />
              <VideoImage
                widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                widgetThumbnailBorder={widgetThumbnailBorder}
                borderRadius={borderRadius}
                isCircle={isCircle}
                loading="lazy"
                src={imageUrl}
                alt={video.name}
              />
            </StyledPicture>
          ) : (
            <StyledPicture>
              <VideoImage
                widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                widgetThumbnailBorder={widgetThumbnailBorder}
                borderRadius={borderRadius}
                isCircle={isCircle}
                loading="lazy"
                src={`https://clipara.b-cdn.net/${video.path}?aspect_ratio=${getAspectRatio(
                  campaign.widgetThumbnailDimensions
                )}&width=${Math.round(videoWidth * 2)}`}
                alt={video.name}
              />
            </StyledPicture>
          )}
          {showNextImage && nextImageUrl && nextWebpUrl ? (
            <StyledNextPicture>
              <source srcset={nextWebpUrl} type="image/webp" />
              <source srcset={nextImageUrl} type="image/png" />
              <VideoImage
                widgetThumbnailBorderSize={widgetThumbnailBorderSize}
                widgetThumbnailBorderColor={widgetThumbnailBorderColor}
                widgetThumbnailBorder={widgetThumbnailBorder}
                borderRadius={borderRadius}
                isCircle={isCircle}
                loading="lazy"
                src={nextImageUrl}
                alt={video.name}
              />
            </StyledNextPicture>
          ) : null}
          {isVideo ? isActive && hasStarted ? null : <InlinePlayButton video={video} pbWidth={60} campaign={campaign} /> : null}
          {isActive && hasStarted && isVideo ? (
            <>
              {/* <PlayContainer autoOpen={campaign.autoOpen}>
                {!isPaused ? (
                  <Volume width="12px" height="18px" alt="volume" src={`${baseUrl}/__clipara-pause-white.svg`} />
                ) : (
                  <Volume ml="4px" width="22px" height="22px" alt="volume" src={`${baseUrl}/__clipara-play-white.svg`} />
                )}
              </PlayContainer> */}
              <MuteContainer
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMuted(!isMuted)
                }}
              >
                <Volume
                  width="28px"
                  height="28px"
                  alt="volume"
                  src={isMuted ? `${baseUrl}/__clipara-mute-white.svg` : `${baseUrl}/__clipara-volume-white.svg`}
                />
              </MuteContainer>
            </>
          ) : null}
          {!isVideo ? (
            <ImageIconContainer>
              <img width="16px" height="16px" alt="close preview" src={`${baseUrl}/__clipara-image-white.svg`} />
            </ImageIconContainer>
          ) : null}

          {titleVideoObject && widgetTitleActive && widgetTitlePosition === 'INSIDE' ? (
            <InnerTitleContainer>
              <Titles
                {...campaign}
                isMobile={isMobile}
                videoWidth={videoWidth}
                gapWidth={widgetGapWidth}
                titleVideoObject={titleVideoObject}
                hideMarginLeft={i === 0}
                hideMarginRight={i === videos.length - 1}
              />
            </InnerTitleContainer>
          ) : null}
          {isActive && hasStarted && isVideo && widgetShowProgressBar ? (
            <ProgressBar campaign={campaign} videoRef={videoRef} forceDesktop />
          ) : null}
        </Box>

        {titleVideoObject && widgetTitleActive && widgetTitlePosition === 'BELOW' ? (
          <Box mt="16px">
            <Titles
              {...campaign}
              isMobile={isMobile}
              videoWidth={videoWidth}
              gapWidth={widgetGapWidth}
              titleVideoObject={titleVideoObject}
              hideMarginLeft={i === 0}
              hideMarginRight={i === videos.length - 1}
            />
          </Box>
        ) : null}
      </CursorDiv>
    </SlideItem>
  )
}

export default InlineSlideItem
